import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faWarehouse, faBoxes } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import warehouseInventoryService from '../../services/warehouseInventoryService';

const InfoRow = ({ icon, label, value }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.25),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {value ?? emDash}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * ProductSizeDistributionModal
 * Shows warehouse distribution for a specific product size
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - productSize: object with product details 
 * - productSizeId: number/string ID if productSize not provided
 */
const ProductSizeDistributionModal = ({ 
  isOpen, 
  onClose, 
  productSize = null, 
  productSizeId = null
}) => {
  const { t } = useTranslation();

  const formatNumber = (n, opts = {}) => {
    if (n === null || n === undefined || n === '') return t('common.symbols.emDash');
    const num = Number(n);
    if (Number.isNaN(num)) return t('common.symbols.emDash');
    const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = opts;
    return num.toLocaleString('en-IN', { minimumFractionDigits, maximumFractionDigits });
  };

  const [loading, setLoading] = useState(false);
  const [distributionData, setDistributionData] = useState(null);
  const [error, setError] = useState(null);

  const resolvedId = productSize?.id || productSizeId;

  useEffect(() => {
    if (!isOpen || !resolvedId) {
      setDistributionData(null);
      setError(null);
      return;
    }

    const controller = new AbortController();

    const fetchDistribution = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await warehouseInventoryService.getProductDistribution(resolvedId, { force: true, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (result.success) {
          setDistributionData(result.data);
        } else {
          setError(result.error || 'Failed to load distribution data');
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err.message || 'Failed to load distribution data');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchDistribution();
    return () => controller.abort();
  }, [isOpen, resolvedId]);

  if (!isOpen) return null;

  const strings = t('productSizes.distribution', { returnObjects: true });

  // Prefer an explicit product name (from provided productSize),
  // otherwise try to read it from the distribution API response if available.
  const productName = productSize?.alias_name || productSize?.product_size || distributionData?.product_name || distributionData?.alias_name || `Product ${resolvedId}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`.product-distribution-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-distribution-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div className="product-distribution-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {productName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              />
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : error ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.05) }}>
              <FontAwesomeIcon
                icon={faWarehouse}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.STATUS.ERROR }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.error.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {error}
              </p>
            </div>
          ) : !distributionData || !Array.isArray(distributionData.warehouses) ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faWarehouse}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              {/* Product Summary */}
              {productSize && (
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoRow icon={faBoxes} label={strings.fields.productName} value={productSize.alias_name} />
                  <InfoRow icon={faBoxes} label={strings.fields.productSize} value={productSize.product_size} />
                </div>
              )}

              {/* Warehouse Distribution */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {strings.fields.warehouseDistribution}
                </h3>

                <div className="space-y-3">
                  {distributionData.warehouses.map((warehouse, index) => (
                    <div
                      key={warehouse.warehouse_id || index}
                      className="flex items-center justify-between rounded-xl border px-4 py-4"
                      style={{
                        borderColor: UI_COLORS.BORDERS.LIGHT,
                        backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="flex h-12 w-12 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
                            color: UI_COLORS.BUTTONS.PRIMARY.background,
                          }}
                        >
                          <FontAwesomeIcon icon={faWarehouse} className="text-lg" />
                        </div>
                        <div>
                          <h4 className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {warehouse.warehouse_name || `Warehouse ${warehouse.warehouse_id}`}
                          </h4>
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className="text-2xl font-bold"
                          style={{ color: UI_COLORS.TEXT.PRIMARY }}
                        >
                          {formatNumber(warehouse.stock_quantity)}
                        </div>
                        <div
                          className="text-sm font-medium"
                          style={{ color: UI_COLORS.TEXT.MUTED }}
                        >
                          {strings.fields.stockQuantity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Summary */}
                <div
                  className="mt-4 flex items-center justify-between rounded-xl border px-4 py-4"
                  style={{
                    borderColor: UI_COLORS.BUTTONS.PRIMARY.background,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.05),
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-lg"
                      style={{
                        backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                        color: UI_COLORS.BUTTONS.PRIMARY.text,
                      }}
                    >
                      <FontAwesomeIcon icon={faBoxes} className="text-lg" />
                    </div>
                    <div>
                      <h4 className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        {strings.fields.totalStock}
                      </h4>
                      <p className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        {t('productSizes.distribution.fields.acrossWarehouses', { count: distributionData.warehouses.length })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className="text-2xl font-bold"
                      style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }}
                    >
                      {formatNumber(distributionData.warehouses.reduce((sum, w) => sum + (Number(w.stock_quantity) || 0), 0))}
                    </div>
                    <div
                      className="text-sm font-medium"
                      style={{ color: UI_COLORS.TEXT.MUTED }}
                    >
                      {strings.fields.totalUnits}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t px-6 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
              color: UI_COLORS.BUTTONS.SECONDARY.text,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSizeDistributionModal;